import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';

import { getDriveApiToken } from '../../lib/auth-token';
type ItemType = 'file' | 'folder';

interface TrashItem {
  id: string;
  name: string;
  type: ItemType;
  size?: number;
  mimetype?: string;
  updatedAt: string;
  thumbnailUrl?: string;
  itemCount?: number;
}

interface TrashState {
  items: TrashItem[];
  loading: boolean;
  error: string | null;
}

const initialState: TrashState = {
  items: [],
  loading: false,
  error: null,
};

// Helper to configure axios with token
const getAxiosConfig = () => {
  const token = getDriveApiToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const fetchTrashedItems = createAsyncThunk(
  'trash/fetchTrashedItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/files/trash', getAxiosConfig());
      const data = response.data;
      let rawItems: any[] = [];
      if (Array.isArray(data)) rawItems = data;
      else if (Array.isArray(data.files)) rawItems = data.files;
      else if (Array.isArray(data.items)) rawItems = data.items;
      // Map to TrashItem shape
      const items: TrashItem[] = rawItems.map(item => ({
        id: item.id,
        name: item.name,
        type: 'file',
        size: typeof item.size === 'string' ? parseInt(item.size, 10) : item.size,
        mimetype: item.mimetype,
        updatedAt: item.updatedAt,
        thumbnailUrl: item.thumbnailUrl,
        itemCount: item.itemCount
      }));
      return items;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trash items');
    }
  }
);

export const restoreItem = createAsyncThunk(
  'trash/restoreItem',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/files/${id}/restore`, {}, getAxiosConfig());
      return { id, ...response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to restore item');
    }
  }
);

export const deleteItemPermanently = createAsyncThunk(
  'trash/deleteItemPermanently',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/files/${id}`, { params: { permanent: true }, ...getAxiosConfig() });
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to permanently delete item');
    }
  }
);

export const emptyTrash = createAsyncThunk(
  'trash/emptyTrash',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/files/trash/empty', {}, getAxiosConfig());
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to empty trash');
    }
  }
);

export const bulkRestoreItems = createAsyncThunk(
  'trash/bulkRestoreItems',
  async (ids: string[], { rejectWithValue }) => {
    try {
      // Execute all restore operations in parallel
      const promises = ids.map(id => 
        api.post(`/files/${id}/restore`, {}, getAxiosConfig())
      );
      await Promise.all(promises);
      return ids;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to restore items');
    }
  }
);

export const bulkDeleteItems = createAsyncThunk(
  'trash/bulkDeleteItems',
  async (ids: string[], { rejectWithValue }) => {
    try {
      // Execute all delete operations in parallel
      const promises = ids.map(id => 
        api.delete(`/files/${id}`, { params: { permanent: true }, ...getAxiosConfig() })
      );
      await Promise.all(promises);
      return ids;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete items');
    }
  }
);

const trashSlice = createSlice({
  name: 'trash',
  initialState,
  reducers: {
    clearTrashError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Trash Items
      .addCase(fetchTrashedItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrashedItems.fulfilled, (state, action: PayloadAction<TrashItem[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTrashedItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Restore Item
      .addCase(restoreItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreItem.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload.id);
      })
      .addCase(restoreItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Item Permanently
      .addCase(deleteItemPermanently.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteItemPermanently.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deleteItemPermanently.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Empty Trash
      .addCase(emptyTrash.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(emptyTrash.fulfilled, (state) => {
        state.loading = false;
        state.items = [];
      })
      .addCase(emptyTrash.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Bulk Restore Items
      .addCase(bulkRestoreItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkRestoreItems.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.loading = false;
        state.items = state.items.filter(item => !action.payload.includes(item.id));
      })
      .addCase(bulkRestoreItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Bulk Delete Items
      .addCase(bulkDeleteItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteItems.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.loading = false;
        state.items = state.items.filter(item => !action.payload.includes(item.id));
      })
      .addCase(bulkDeleteItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTrashError } = trashSlice.actions;
export default trashSlice.reducer; 